import { useState } from "react";
import { Dropdown, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import Swal from "sweetalert2";

import AlertModal from "components/AlertModal";
import * as C from "components/EntityCard/styles";
import ModalComponent from "components/ModalComponent";
import WppAttendance from "components/WppAttendance";
import { useUser } from "providers/User";
import { api } from "services";
import { PwdRecoveryStepOneResult } from "types/auth";
import { User } from "types/user";
import { cpfAndCnpjMask, mobilePhoneMask } from "utils/formMasks";
import { toast } from "utils/toast";

import {
  autoCloseValidationMessage,
  pwdRecoverySteps2and3,
} from "../../utils/pwdRecoveryPopUp";
import UserEditForm from "../Forms/UserEditForm";

import NewPwdAdminForm from "./components/NewPwdAdminForm";

interface UserCardProps {
  user: User;
  isActive?: boolean;
}

const getInitials = (name: string) => {
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const UserCard = ({ user, isActive = false }: UserCardProps) => {
  const [alertModalShow, setAlertModalShow] = useState(false);
  const [resetPwdModalShow, setResetPwdModalShow] = useState(false);
  const [userEditModalShow, setUserEditModalShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    setInactiveUsersCurrentPage,
    setActiveUsersCurrentPage,
    getInactiveUsers,
    getActiveUsers,
  } = useUser();

  const handleActivateUser = async () => {
    setIsLoading(true);
    try {
      await api.patch(`/User/admin/${user.id}/${isActive ? "disable" : "enable"}`);
      setIsLoading(false);
      toast.fire({ icon: "success", title: `Usuário ${isActive ? "des" : ""}ativado com sucesso!` });
      setInactiveUsersCurrentPage(1);
      setActiveUsersCurrentPage(1);
      getInactiveUsers();
      getActiveUsers();
      setAlertModalShow(false);
    } catch {
      setIsLoading(false);
      toast.fire({ icon: "error", title: "Ocorreu um erro, tente novamente." });
    }
  };

  const openPwdRecoveryPopUp = async () => {
    Swal.showLoading();
    Swal.resetValidationMessage();
    try {
      const response = await api.get(`/Auth/recovery-contacts/${user.cpf}`);
      const resultStepOne: PwdRecoveryStepOneResult = {
        request: { emailOrCpf: user.cpf },
        response: response.data,
      };
      Swal.hideLoading();
      pwdRecoverySteps2and3(user.cpf, <WppAttendance />, resultStepOne);
    } catch {
      Swal.hideLoading();
      autoCloseValidationMessage(
        "CPF/e-mail não encontrado no sistema. Verifique seus dados e tente novamente.",
        7000
      );
    }
  };

  const userName = user.socialName ? `${user.socialName} (${user.name})` : user.name;
  const phone = mobilePhoneMask(`${user.dddMobilePhone}${user.mobilePhone}`);
  const address = user.addressCEP
    ? `${user.addressRoad}, ${user.addressNumber}, ${user.addressCity} - ${user.addressState}`
    : null;

  return (
    <>
      <C.Card
        style={{ "--left-color": isActive ? "var(--primary)" : "var(--gray)" } as React.CSSProperties}
        aria-label={`Usuário: ${userName}`}
      >
        <C.CardHeader>
          <C.AvatarCircle aria-hidden="true">{getInitials(user.name)}</C.AvatarCircle>

          <OverlayTrigger placement="top" overlay={<Tooltip>{userName}</Tooltip>}>
            <C.EntityName>{userName}</C.EntityName>
          </OverlayTrigger>

          <Dropdown align="end">
            <Dropdown.Toggle as="div" bsPrefix="no-caret">
              <C.ActionButton aria-label={`Ações para ${userName}`} aria-haspopup="true">
                <i className="uil uil-ellipsis-v" aria-hidden="true" />
              </C.ActionButton>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item as="button" onClick={() => setUserEditModalShow(true)}>
                <i className="uil uil-edit-alt me-2" aria-hidden="true" />
                Editar
              </Dropdown.Item>
              <Dropdown.Item as="button" onClick={openPwdRecoveryPopUp}>
                <i className="uil uil-envelope-alt me-2" aria-hidden="true" />
                Recuperar senha
              </Dropdown.Item>
              <Dropdown.Item as="button" onClick={() => setResetPwdModalShow(true)}>
                <i className="uil uil-key-skeleton-alt me-2" aria-hidden="true" />
                Redefinir senha
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                as="button"
                className={isActive ? "text-danger" : "text-success"}
                onClick={() => setAlertModalShow(true)}
              >
                <i className={`uil uil-${isActive ? "times" : "check"}-circle me-2`} aria-hidden="true" />
                {isActive ? "Desativar" : "Ativar"}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </C.CardHeader>

        <C.CardBody aria-label="Dados do usuário">
          <C.MetaItem>
            <C.MetaLabel>CPF</C.MetaLabel>
            <C.MetaValue>{cpfAndCnpjMask(user.cpf)}</C.MetaValue>
          </C.MetaItem>

          <C.MetaItem>
            <C.MetaLabel>Telefone</C.MetaLabel>
            <C.MetaValue>{phone || "—"}</C.MetaValue>
          </C.MetaItem>

          <C.MetaItem>
            <C.MetaLabel>E-mail</C.MetaLabel>
            <OverlayTrigger placement="top" overlay={<Tooltip>{user.email}</Tooltip>}>
              <C.MetaValue>{user.email || "—"}</C.MetaValue>
            </OverlayTrigger>
          </C.MetaItem>

          <C.MetaItem>
            <C.MetaLabel>Endereço</C.MetaLabel>
            {address ? (
              <OverlayTrigger placement="top" overlay={<Tooltip>{address}</Tooltip>}>
                <C.MetaValue>{address}</C.MetaValue>
              </OverlayTrigger>
            ) : (
              <C.MetaValueMuted>Não informado</C.MetaValueMuted>
            )}
          </C.MetaItem>
        </C.CardBody>
      </C.Card>

      <AlertModal
        show={alertModalShow}
        onHide={() => setAlertModalShow(false)}
        title={`Tem certeza que deseja ${isActive ? "des" : ""}ativar esse usuário?`}
        onClickButton={handleActivateUser}
        btnVariant={isActive ? "danger" : "success"}
        isLoading={isLoading}
      />
      <ModalComponent
        title="Editar Usuário"
        show={userEditModalShow}
        onHide={() => setUserEditModalShow(false)}
        size="xl"
      >
        <UserEditForm onHide={() => setUserEditModalShow(false)} user={user} />
      </ModalComponent>
      <Modal
        backdrop="static"
        centered
        show={resetPwdModalShow}
        onHide={() => setResetPwdModalShow(false)}
      >
        <Modal.Header closeButton className="justify-content-end pb-0 border-0" />
        <Modal.Body className="text-body">
          <div className="text-center">
            <p><i className="uil uil-user-circle display-4" /></p>
            <h4 className="mb-4">Defina uma nova senha para esse usuário:</h4>
            <NewPwdAdminForm userId={user.id as number} onHide={() => setResetPwdModalShow(false)} />
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default UserCard;
